import { Button, Dialog, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { WorkflowGroup } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { getObjectPageRoute } from "../../../../utilities/navigationHelper";
import type { DocumentPollingScheduler } from "./documentPolling";
import { createDocumentPollingScheduler } from "./documentPolling";
import {
  acceptedFileExtensions,
  findInvoiceByCatalogueId,
  matchWorkflowDocuments,
  MAX_POLL_CONCURRENCY,
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
  pollBySourceId,
  processQueueResults,
  runDocumentWorkflows,
  uploadDocumentWorkflowFile,
} from "./documentWorkflowService";
import UploadDocumentsFileRow from "./UploadDocumentsFileRow";
import type { FileEntry } from "./uploadDocumentsState";
import {
  addFilesAction,
  cancelAnalysisAction,
  classifyFiles,
  initialUploadDocumentsState,
  markWorkflowStartedAction,
  removeFileAction,
  selectWorkflowAction,
  setAnalysisFailedAction,
  setAnalysisResultAction,
  setCatalogueIdAction,
  setQueueResultAction,
  setStatusAction,
  startAnalysisAction,
  startQueueingAction,
} from "./uploadDocumentsState";

const acceptedFileExtensionsWithXml = [...acceptedFileExtensions, ".xml"];

export type UploadDocumentsMode =
  { kind: "match"; workflowGroups: WorkflowGroup[] } | { kind: "run"; workflowType: WorkflowType; templateId: string };

interface Props {
  onClose: (workflowsStarted: boolean) => void;
  mode: UploadDocumentsMode;
}

const UploadDocumentsDialog = ({ onClose, mode }: Props) => {
  const [state, setState] = useState(initialUploadDocumentsState);
  const [previewFile, setPreviewFile] = useState<{ file: File; name: string }>();
  const { clientCode, isPortalFeatureEnabled } = useClientContext();
  const [fileIdToCatalogIdMap] = useState(() => new Map<string, string>());
  const cancelledAnalysisRef = useRef(new Set<string>());

  const templateId = mode.kind === "run" ? mode.templateId : undefined;

  const [pollingScheduler] = useState<DocumentPollingScheduler>(() =>
    createDocumentPollingScheduler({
      pollBySourceId,
      onQueueTimeout: (fileId) => {
        setState(setQueueResultAction(fileId, "QueueFailed", undefined, "Workflow start timed out. Please try again."));
      },
      onWorkflowStarted: async (fileId, workflow) => {
        const tplId = workflow.templateId || templateId;
        let workflowDestination = tplId
          ? `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowRuns}/${tplId}/${workflow.workflowId}`
          : undefined;

        if (workflow.workflowType === "InvoiceProcessing") {
          const catalogueId = fileIdToCatalogIdMap.get(fileId);
          if (catalogueId) {
            const invoice = await findInvoiceByCatalogueId(catalogueId);
            if (invoice) {
              workflowDestination = getObjectPageRoute(clientCode, invoice.id, "Invoice");
            }
          }
        }

        setState(
          markWorkflowStartedAction(
            fileId,
            workflow.workflowId,
            workflow.templateId,
            mode.kind === "match" ? workflow.workflowType : undefined,
            workflowDestination
          )
        );
      },
      onError: (error) => {
        logError(error, "[UploadDocumentsDialog] getDocumentWorkflowBySourceId");
      },
      now: () => Date.now(),
      timeoutMs: POLL_TIMEOUT_MS,
      maxConcurrency: MAX_POLL_CONCURRENCY,
    })
  );

  const previewUrl = useMemo(() => {
    if (!previewFile) {
      return undefined;
    }
    return URL.createObjectURL(previewFile.file);
  }, [previewFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // eslint-disable-next-line @eslint-react/immutability -- the scheduler is a mutable instance kept for the dialog's lifetime
  useEffect(() => {
    return () => {
      // eslint-disable-next-line @eslint-react/immutability -- see above
      pollingScheduler.clear();
    };
  }, [pollingScheduler]);

  const handlePreview = (id: string) => {
    const entry = state.files.find((f) => f.id === id);
    if (entry) {
      setPreviewFile({ file: entry.file, name: entry.file.name });
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(undefined);
  };

  const analyzeFile = async (id: string, catalogueId: string) => {
    const [matchResult, matchError] = await matchWorkflowDocuments({ catalogueIds: [catalogueId] });
    if (matchError) {
      logError(matchError, "[UploadDocumentsDialog] matchDocuments");
      setState(setAnalysisFailedAction(id, matchError.message));
      return;
    }

    const bestDecision = matchResult.bestMatch
      ? matchResult.decisions.find((d) => d.workflowType === matchResult.bestMatch)
      : matchResult.decisions[0];

    setState(setAnalysisResultAction(id, bestDecision, mode.kind === "match" ? bestDecision?.workflowType : undefined));
  };

  const processFile = async (id: string, file: File) => {
    const [uploadResult, uploadError] = await uploadDocumentWorkflowFile(file);
    if (uploadError) {
      logError(uploadError, "[UploadDocumentsDialog] uploadFile");
      setState(setStatusAction(id, "UploadFailed", uploadError.message));
      return;
    }

    const catalogueId = uploadResult.catalogueId;
    // eslint-disable-next-line @eslint-react/immutability -- lookup map is mutable by design; changes must not re-render
    fileIdToCatalogIdMap.set(id, catalogueId);

    if (mode.kind === "match" && !cancelledAnalysisRef.current.has(id)) {
      setState((prev) => startAnalysisAction([id])(setCatalogueIdAction(id, catalogueId)(prev)));
      analyzeFile(id, catalogueId);
    } else {
      setState(setCatalogueIdAction(id, catalogueId));
    }
  };

  const handleFilesAdd = (files: File[]) => {
    const { unique, duplicates } = classifyFiles(files, state.files);
    const entries = [...unique, ...duplicates];
    setState(addFilesAction(entries));

    for (const entry of unique) {
      processFile(entry.id, entry.file);
    }
  };

  const handleRetry = (id: string) => {
    const entry = state.files.find((f) => f.id === id);
    if (!entry) {
      return;
    }

    if (entry.status === "UploadFailed") {
      cancelledAnalysisRef.current.delete(id);
      setState(setStatusAction(id, "Uploading"));
      processFile(id, entry.file);
    } else if (entry.status === "QueueFailed" && entry.catalogueId) {
      queueFiles([entry]);
    } else if (
      mode.kind === "match" &&
      entry.status === "Uploaded" &&
      entry.analysisStatus === "Failed" &&
      entry.catalogueId
    ) {
      setState(startAnalysisAction([id]));
      analyzeFile(id, entry.catalogueId);
    }
  };

  const handleCancel = (id: string) => {
    cancelledAnalysisRef.current.add(id);
    setState(cancelAnalysisAction(id));
  };

  const handleRemove = (id: string) => {
    pollingScheduler.remove(id);
    cancelledAnalysisRef.current.delete(id);
    setState(removeFileAction(id));
  };

  const handleSelectWorkflow = (id: string, workflowType: WorkflowType) => {
    setState(selectWorkflowAction(id, workflowType));
  };

  // Run mode: optional analysis
  const analyzableFiles =
    mode.kind === "run"
      ? state.files.filter(
          (f) =>
            f.status === "Uploaded" &&
            f.catalogueId &&
            f.analysisStatus !== "Analyzing" &&
            f.analysisStatus !== "Analyzed"
        )
      : [];

  const isAnalyzing = state.files.some((f) => f.analysisStatus === "Analyzing");

  const handleAnalyze = () => {
    if (analyzableFiles.length === 0) {
      return;
    }

    setState(startAnalysisAction(analyzableFiles.map((f) => f.id)));

    for (const file of analyzableFiles) {
      analyzeFile(file.id, defined(file.catalogueId));
    }
  };

  // Runnable files
  const runnableFiles = state.files.filter((f) => {
    if (f.status !== "Uploaded" && f.status !== "QueueFailed") return false;
    if (!f.catalogueId) return false;
    if (mode.kind === "match") {
      if (!f.selectedWorkflowType) return false;
      if (f.analysisStatus === "Analyzing") return false;
    }
    return true;
  });

  const queueFiles = async (filesToQueue: readonly FileEntry[]) => {
    if (filesToQueue.length === 0) {
      return;
    }

    for (const file of filesToQueue) {
      pollingScheduler.remove(file.id);
    }

    setState(startQueueingAction(filesToQueue.map((file) => file.id)));

    const [runResult, runError] = await runDocumentWorkflows({
      items: filesToQueue.map((file) => ({
        workflowType: mode.kind === "match" ? defined(file.selectedWorkflowType) : mode.workflowType,
        selectedCatalogueIds: [defined(file.catalogueId)],
        isFileContentAlignedWithWorkflowType: true,
      })),
    });

    if (runError) {
      logError(runError, "[UploadDocumentsDialog] runDocumentWorkflows");
      for (const file of filesToQueue) {
        pollingScheduler.remove(file.id);
        setState(setQueueResultAction(file.id, "QueueFailed", undefined, runError.message));
      }
      return;
    }

    processQueueResults(
      filesToQueue,
      runResult.items,
      pollingScheduler,
      (id, sourceId) => setState(setQueueResultAction(id, "Queued", sourceId)),
      (id, runError) => setState(setQueueResultAction(id, "QueueFailed", undefined, runError))
    );
  };

  const handleRun = async () => {
    await queueFiles(runnableFiles);
  };

  useSetInterval(() => pollingScheduler.tick(), POLL_INTERVAL_MS);

  const handleClose = () => {
    const workflowsStarted = state.files.some((f) => f.status === "WorkflowStarted");
    onClose(workflowsStarted);
  };

  const isXmlAccepted =
    isPortalFeatureEnabled("ExpenseManagementEInvoicingEnabled") &&
    (mode.kind === "match" || mode.workflowType === "InvoiceProcessing");

  return (
    <>
      <Dialog open onClose={handleClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { maxHeight: "80vh" } } }}>
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogCloseButton onClick={handleClose} />

        <DialogContent>
          <Stack spacing={2} sx={{ minHeight: "20rem" }}>
            <FilesDropArea
              allowMultiple
              acceptedFileExtensions={isXmlAccepted ? acceptedFileExtensionsWithXml : acceptedFileExtensions}
              subtitle={`Maximum file size: 25 MB. Supported formats: pdf, doc(x), xls(x)(m), ppt(x), jpg, jpeg, png, webp${isXmlAccepted ? ", xml" : ""}.`}
              onFilesAdd={handleFilesAdd}
              containerProps={{ sx: { flex: state.files.length > 0 ? undefined : 1 } }}
            />

            {state.files.length > 0 && (
              <Stack spacing={0.5}>
                {state.files.map((entry) => (
                  <UploadDocumentsFileRow
                    key={entry.id}
                    entry={entry}
                    mode={mode}
                    onPreview={handlePreview}
                    onCancel={mode.kind === "match" ? handleCancel : undefined}
                    onRetry={handleRetry}
                    onRemove={handleRemove}
                    onSelectWorkflow={mode.kind === "match" ? handleSelectWorkflow : undefined}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogButtons onCancel={handleClose} cancelText="Close">
          {mode.kind === "run" && (
            <Button variant="outlined" disabled={analyzableFiles.length === 0 || isAnalyzing} onClick={handleAnalyze}>
              {isAnalyzing
                ? "Analyzing\u2026"
                : analyzableFiles.length > 0
                  ? `Analyze (${analyzableFiles.length})`
                  : "Analyze"}
            </Button>
          )}
          <Button variant="contained" disabled={runnableFiles.length === 0} onClick={handleRun}>
            {runnableFiles.length > 0 ? `Run (${runnableFiles.length})` : "Run"}
          </Button>
        </DialogButtons>
      </Dialog>
      {previewUrl && previewFile && (
        <PreviewFileDialog url={previewUrl} fileName={previewFile.name} onClose={handleClosePreview} />
      )}
    </>
  );
};

export default UploadDocumentsDialog;
